import { useQuery } from '@tanstack/react-query';
import { http, qs } from './api';
import type { AiStatus, IssueType, Project, Release, Sprint, Status, User } from './types';

export const useMe = () =>
  useQuery({ queryKey: ['me'], queryFn: () => http.get<{ user: User; org: { id: string; name: string; timezone: string }; projectIds: string[] }>('/me'), staleTime: 60_000 });

export const useProjects = () => useQuery({ queryKey: ['projects'], queryFn: () => http.get<Project[]>('/projects'), staleTime: 60_000 });

export const useProject = (key?: string) =>
  useQuery({ queryKey: ['project', key], queryFn: () => http.get<Project>(`/projects/${key}`), enabled: !!key, staleTime: 30_000 });

export const useStatuses = (key?: string) =>
  useQuery({ queryKey: ['statuses', key], queryFn: () => http.get<Status[]>(`/projects/${key}/statuses`), enabled: !!key, staleTime: 5 * 60_000 });

export const useIssueTypes = (key?: string) =>
  useQuery({ queryKey: ['issue-types', key], queryFn: () => http.get<IssueType[]>(`/projects/${key}/issue-types`), enabled: !!key, staleTime: 5 * 60_000 });

export const useLabels = (key?: string) =>
  useQuery({ queryKey: ['labels', key], queryFn: () => http.get<{ id: string; name: string; color: string }[]>(`/projects/${key}/labels`), enabled: !!key, staleTime: 60_000 });

export const useComponents = (key?: string) =>
  useQuery({
    queryKey: ['components', key],
    queryFn: () => http.get<{ id: string; name: string; defaultDevOwnerId: string | null; defaultQaOwnerId: string | null }[]>(`/projects/${key}/components`),
    enabled: !!key,
    staleTime: 60_000,
  });

export const useResolutions = (key?: string) =>
  useQuery({ queryKey: ['resolutions', key], queryFn: () => http.get<{ id: string; name: string; category: string }[]>(`/projects/${key}/resolutions`), enabled: !!key, staleTime: 5 * 60_000 });

export const useReleases = (key?: string) =>
  useQuery({ queryKey: ['releases', key], queryFn: () => http.get<Release[]>(`/projects/${key}/releases`), enabled: !!key, staleTime: 30_000 });

export const useSprints = (key?: string) =>
  useQuery({ queryKey: ['sprints', key], queryFn: () => http.get<Sprint[]>(`/projects/${key}/sprints`), enabled: !!key, staleTime: 30_000 });

export const useAllSprints = (key?: string) =>
  useQuery({ queryKey: ['sprints', key, 'all'], queryFn: () => http.get<Sprint[]>(`/projects/${key}/sprints?all=true`), enabled: !!key, staleTime: 30_000 });

export const useAssignable = (key?: string) =>
  useQuery({ queryKey: ['assignable', key], queryFn: () => http.get<User[]>(`/projects/${key}/assignable-users`), enabled: !!key, staleTime: 60_000 });

export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: () => http.get<User[]>('/users'), staleTime: 5 * 60_000 });

export const useUserMap = () => {
  const { data } = useUsers();
  return new Map((data ?? []).map((u) => [u.id, u]));
};

export const useTeams = () => useQuery({ queryKey: ['teams'], queryFn: () => http.get<{ id: string; name: string; handle: string; memberIds: string[] }[]>('/teams'), staleTime: 5 * 60_000 });

export const useAiStatus = (projectKey?: string) =>
  useQuery({ queryKey: ['ai-status', projectKey], queryFn: () => http.get<AiStatus>(`/ai/status${qs({ project: projectKey })}`), staleTime: 60_000 });

export const useUnread = () =>
  useQuery({ queryKey: ['notifications', 'count'], queryFn: () => http.get<{ unread: number }>('/notifications/unread-count'), refetchInterval: 60_000 });

export const useTimer = () =>
  useQuery({ queryKey: ['timer'], queryFn: () => http.get<{ id: string; startedAt: string; item: { key: string; title: string } | null } | null>('/timers/current') });

export interface CustomFieldDef {
  id: string;
  fieldKey: string;
  label: string;
  dataType: 'text' | 'number' | 'date' | 'select' | 'multi_select' | 'user' | 'url';
  options: string[] | null;
  required: boolean;
  appliesToIssueTypes: string[];
}
export const useCustomFields = (key?: string) =>
  useQuery({ queryKey: ['custom-fields', key], queryFn: () => http.get<CustomFieldDef[]>(`/projects/${key}/custom-fields`), enabled: !!key, staleTime: 60_000 });
